'use client'

import { ClientSelect } from '@repo/ui/ClientSelect'
import { LargeMatterIcon } from '@repo/ui/LargeMatterIcon'
import { LargeUserIcon } from '@repo/ui/LargeUserIcon'
import { MatterSelectByClient } from '@repo/ui/MatterSelectByClient'
import { useNewClientStore } from '@repo/store/useNewClientStore'
import { useNewMatterStore } from '@repo/store/useNewMatterStore'
import { useNotificationStore } from '@repo/store/useNotificationStore'
import { useState, useEffect } from 'react'
import { WizardFooter } from '@repo/ui/WizardFooter'
import { WizardIconTitle } from '@repo/ui/WizardIconTitle'
import { WizardPanel } from '@repo/ui/WizardPanel'
import MUIFormControlLabel from '@mui/material/FormControlLabel'
import MUIRadio from '@mui/material/Radio'
import MUIRadioGroup from '@mui/material/RadioGroup'
import MUITypography from '@mui/material/Typography'
import PropTypes from 'prop-types'

export const COISSelectClientMatter = ({ updateStepState, wideMode, handlePrevNext, changeWizard = () => {} }) => {
  const {
    updateState: updateNewClientState,
    existingClient,
    isNew: clientIsNew,
    newClientNo,
    reset: resetNewClientStore
  } = useNewClientStore()
  const {
    updateState: updateNewMatterState,
    existingMatter,
    isNew: matterIsNew,
    newMatterNo,
    reset: resetNewMatterStore
  } = useNewMatterStore()
  const { isNotificationWizard } = useNotificationStore()

  const [validationErrors, setValidationErrors] = useState({})

  const handleClickContinue = () => {
    const validationErrors = {}

    if (!clientIsNew && !existingClient) {
      validationErrors.client = 'Please select a client'
    }

    if (!matterIsNew && !existingMatter) {
      validationErrors.matter = 'Please select a matter'
    }

    setValidationErrors(validationErrors)
    if (Object.keys(validationErrors).length) return

    updateStepState({ status: 'completed', statusText: 'Complete' })
    handlePrevNext(1)
  }

  const handleClickBack = () => {
    handlePrevNext(-1)
  }

  useEffect(() => {
    if (isNotificationWizard === false) {
      // clear the existing values if new client/matter is selected
      if (clientIsNew) resetNewClientStore()
      if (matterIsNew) resetNewMatterStore()

      // clear the new client/matter no if existing client/matter is selected
      if (!clientIsNew && newClientNo) updateNewClientState({ key: 'newClientNo', value: '' })
      if (!matterIsNew && newMatterNo) updateNewMatterState({ key: 'newMatterNo', value: '' })

      // update wizard config

      if (clientIsNew && matterIsNew) {
        changeWizard('COIS-NewClient-NewMatter')
      } else if (!clientIsNew && matterIsNew) {
        changeWizard('COIS-ExistingClient-NewMatter')
      } else {
        changeWizard('COIS-ExistingClient-ExistingMatter')
      }
    } else {
      !clientIsNew && newClientNo && updateNewClientState({ key: 'newClientNo', value: '' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientIsNew, matterIsNew])

  useEffect(() => {
    if (clientIsNew) updateNewMatterState({ key: 'isNew', value: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientIsNew])

  return (
    <>
      <WizardPanel data-testid="COISSelectClientMatter">
        <WizardIconTitle title="Client details" icon={LargeUserIcon} sx={{ mb: 1 }} />

        <MUITypography variant="body1" sx={{ mb: 4, mt: 4 }}>
          Would you like to create a new client, or add this conflict of interest search to an existing one?
        </MUITypography>

        <MUIRadioGroup
          value={clientIsNew ? 'new' : 'existing'}
          name="newClient"
          sx={{
            width: '100%',
            pointerEvents: isNotificationWizard ? 'none' : 'auto',
            opacity: isNotificationWizard ? 0.75 : 1
          }}
          onChange={(e) => updateNewClientState({ key: 'isNew', value: e.target.value === 'new' })}
        >
          <MUIFormControlLabel value="new" control={<MUIRadio />} label="New client" disabled={isNotificationWizard} />
          <MUIFormControlLabel
            value="existing"
            control={<MUIRadio />}
            label="Existing client"
            disabled={isNotificationWizard}
          />
        </MUIRadioGroup>

        {clientIsNew ? null : (
          <ClientSelect
            size="medium"
            sx={{
              width: '400px',
              marginTop: '20px',
              pointerEvents: isNotificationWizard ? 'none' : 'auto',
              opacity: isNotificationWizard ? 0.75 : 1
            }}
            value={existingClient}
            onChange={(value) => updateNewClientState({ key: 'existingClient', value })}
            required
            name="client"
            errors={validationErrors}
          />
        )}
      </WizardPanel>

      <WizardPanel sx={{ mt: 6 }}>
        <WizardIconTitle title="Matter details" icon={LargeMatterIcon} sx={{ mb: 1 }} />

        <MUITypography variant="body1" sx={{ mb: 4, mt: 4 }}>
          Would you like to create a new matter, or add this conflict of interest search to an existing one?
        </MUITypography>

        <MUIRadioGroup
          value={matterIsNew ? 'new' : 'existing'}
          name="newMatter"
          sx={{
            width: '100%',
            pointerEvents: clientIsNew || isNotificationWizard ? 'none' : 'auto',
            opacity: clientIsNew || isNotificationWizard ? 0.75 : 1
          }}
          onChange={(e) => updateNewMatterState({ key: 'isNew', value: e.target.value === 'new' })}
        >
          <MUIFormControlLabel value="new" control={<MUIRadio />} label="New matter" />
          <MUIFormControlLabel
            value="existing"
            control={<MUIRadio />}
            label="Existing matter"
            disabled={isNotificationWizard}
          />
        </MUIRadioGroup>

        {matterIsNew ? null : (
          <MatterSelectByClient
            size="medium"
            sx={{ width: '400px', marginTop: '20px' }}
            value={existingMatter}
            onChange={(value) => updateNewMatterState({ key: 'existingMatter', value })}
            required
            name="matter"
            errors={validationErrors}
            clientNo={existingClient?.clientNo || ''}
          />
        )}

        {!matterIsNew && !existingClient ? (
          <MUITypography variant="body2" sx={{ mb: 4, mt: 1, color: 'text.secondary' }}>
            Please select a client first
          </MUITypography>
        ) : null}
      </WizardPanel>

      <WizardFooter wideMode={wideMode} handleClickContinue={handleClickContinue} handleClickBack={handleClickBack} />
    </>
  )
}

COISSelectClientMatter.propTypes = {
  updateStepState: PropTypes.func,
  currentStep: PropTypes.object,
  wideMode: PropTypes.bool,
  handlePrevNext: PropTypes.func,
  changeWizard: PropTypes.func
}
